/**
 * PROTECTED GATE LOGIC - This is intentional and documented.
 * Any changes require approval per Product UX Playbook.
 * 
 * Rules:
 * 1. First product page = ungated
 * 2. Gate triggers on 2nd product interaction only
 * 3. One gate per session maximum
 * 4. Dismissible with session memory
 * 5. No gate on: Homepage, first product page, ad landings
 * 6. No Pro or pricing references in this modal
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, X } from 'lucide-react';
import { authService } from '@/lib/auth';
import { toast } from '@/hooks/use-toast';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { trackGateEvent } from '@/data/gateAnalytics';

interface ProductGateOverlayProps {
  open: boolean;
  productId: string | undefined;
  gateShownAt: number | null;
  onSignupSuccess: () => void;
  onClose: () => void;
}

const signupSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }),
  password: z.string().min(8, { message: "Password must be at least 8 characters" }),
  fullName: z.string().trim().min(2, { message: "Name must be at least 2 characters" }),
});

// Hardcoded approved copy - no config-driven content
const GATE_COPY = {
  headline: "Create a free account to keep exploring",
  subtext: "Create a free account to compare vendors and save options.",
  primaryCta: "Create Free Account",
  signInText: "Already have an account?",
  signInLink: "Sign In",
  frictionReducer: "Free forever. No credit card."
};

export function ProductGateOverlay({ 
  open, 
  productId, 
  gateShownAt, 
  onSignupSuccess,
  onClose 
}: ProductGateOverlayProps) {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleDismiss = () => {
    const timeOnGate = gateShownAt ? Date.now() - gateShownAt : undefined;
    trackGateEvent('dismiss', productId, undefined, timeOnGate);
    onClose();
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate input
    const result = signupSchema.safeParse({ email, password, fullName });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      const { error } = await authService.signUp(email, password, fullName);
      
      if (error) {
        if (error.message.includes('already registered')) {
          setErrors({ email: 'This email is already registered. Try signing in instead.' });
        } else {
          toast({
            variant: "destructive",
            title: "Signup failed",
            description: error.message,
          });
        }
        return;
      }

      // Success - track conversion
      const timeOnGate = gateShownAt ? Date.now() - gateShownAt : undefined;
      await trackGateEvent('signup', productId, undefined, timeOnGate);
      
      toast({
        title: "Account created!",
        description: "Welcome to Circle Network. Check your email to confirm your account.",
      });
      onSignupSuccess();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = () => {
    handleDismiss();
    navigate('/auth?mode=signin');
  };

  return (
    <DialogPrimitive.Root open={open}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay 
          className="fixed inset-0 z-50 bg-black/45 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" 
          onClick={handleDismiss}
        />
        <DialogPrimitive.Content
          onInteractOutside={handleDismiss}
          onEscapeKeyDown={handleDismiss}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "max-w-md w-[calc(100%-2rem)] p-4 md:p-6",
            "bg-background border border-border/50 rounded-xl shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          )}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className="text-center mb-5 pr-6">
            <h2 className="text-xl md:text-2xl font-bold mb-2">
              {GATE_COPY.headline}
            </h2>
            <p className="text-sm text-muted-foreground">
              {GATE_COPY.subtext}
            </p>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSignup} className="space-y-3">
            <div>
              <Label htmlFor="fullName" className="text-sm">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
                disabled={loading}
                className={cn("h-10", errors.fullName && 'border-destructive')}
              />
              {errors.fullName && (
                <p className="text-xs text-destructive mt-1">{errors.fullName}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={loading}
                className={cn("h-10", errors.email && 'border-destructive')}
              />
              {errors.email && (
                <p className="text-xs text-destructive mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-sm">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading}
                  className={cn("h-10", errors.password && 'border-destructive')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-destructive mt-1">{errors.password}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-10 mt-4"
              disabled={loading}
            >
              {loading ? 'Creating account...' : GATE_COPY.primaryCta}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground">
              {GATE_COPY.signInText}{' '}
              <button
                type="button"
                onClick={handleSignIn}
                className="text-primary hover:underline font-medium"
              >
                {GATE_COPY.signInLink}
              </button>
            </p>
          </div>

          {/* Friction Reducer */}
          <p className="text-xs text-center text-muted-foreground mt-4">
            {GATE_COPY.frictionReducer}
          </p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
